/**
 * Research host — the top-level `research` view body (the Wire pass the fidelity
 * audit called for, docs/fidelity-audit-2026-07.md → Research TIER 1: "весь поток
 * недостижим"). It owns the internal screen state machine that stitches the
 * previously-orphaned research screens into one working flow:
 *
 *   library|history  →  intake  →  run  →  review  →  publish  →  (back to library)
 *                                      ↘  aichain  ↗
 *
 * Each screen already exists and is unit-tested; this host does NOT rewrite them —
 * it mounts them and routes between them:
 *   - ResearchLibraryView  — Library + History browser (owns its own library↔history
 *                            panel switch internally); its `onOpenScreen(screen, ctx)`
 *                            callback drives THIS host's screen state.
 *   - IntakeForm           — New / cloned request; `onCreated(requestId)` → start run.
 *   - ResearchRunView      — the Run/Progress screen (created for this pass);
 *                            `onReview` → Review; `onAiCheck` → AI check; Cancel →
 *                            back to library.
 *   - ResearchAiChainView  — the manual paid AI-check screen (E06-F05-T05); reached
 *                            from Run's "AI check" button, `onBack` → Run,
 *                            `onReview` → Review. Discovery stops at `deduped`, so
 *                            this side-branch is where the money is actually spent —
 *                            always by an explicit click on that screen, never by
 *                            navigating here.
 *   - ResearchReviewView   — the long-list review gate; `onPublish` → Publish.
 *   - ResearchPublishView  — export/send/save; `onBackToLibrary` → back to library.
 *
 * The `ctx` (requestId + status + optional prefilled criteria for a clone) is the
 * single carried datum. Note the shared `ResearchScreen` vocab is `intake|run|review`
 * only — `library` (the entry point) and `publish` (the terminal step) are host-local
 * screens, so the host uses its own `HostScreen` union that is a superset.
 *
 * E06-F02-T11 — real "back": every screen switch goes through `go()`, which also
 * writes a browser-history entry (same URL, `view: 'research'` kept so the shell's
 * router stays on Research). The browser/OS back gesture therefore walks back
 * through the screens the recruiter actually visited, and a `popstate` restores
 * that screen. The Library/History browser state (panel + History search / status /
 * page) is held HERE, above the browser that unmounts on every screen switch, so
 * coming back (browser back, or the "Back to History" button on the flow screens)
 * shows the same filtered list instead of a reset Library panel.
 * The entry shape lives in `./researchNavModel.ts` (unit-tested).
 *
 * E06-F02-T12 — every flow screen (Intake, Run, AI check, Review, Publish) also has
 * a generic "Back" to the PREVIOUS screen: it walks the Research history entries
 * (`history.back()`) while there is an earlier Research screen, else falls back to
 * the screen's logical parent. The Intake form reports its values up (`onDraftChange`)
 * and they are stamped into the Intake entry on leaving it, so coming back to the
 * form (Back from Run, browser back) re-opens it with the criteria, title and project
 * link the recruiter had entered. Run / Review / … state is server-side per request
 * id, which every entry carries, so those screens come back as they were.
 *
 * E12-F17-T07 — a saved DRAFT opened from History («Open → Request», ctx.requestId > 0
 * with no carried criteria) is LOADED (GET /research/requests/:id) before the form
 * mounts, so it shows the saved title / criteria / project / Global DNS; the form
 * then edits THAT draft (`draftRequestId` → PATCH), and a 409 «identical criteria»
 * offers to open the existing draft (`onOpenDraft`).
 */
import { useEffect, useRef, useState, type ComponentProps, type JSX } from 'react';
import type { IntakeCriteria, ResearchScreen } from '@eer/shared';
import { ResearchLibraryView, type OpenScreenContext } from './ResearchLibraryView.js';
import { IntakeForm } from './IntakeForm.js';
import { ResearchRunView } from './ResearchRunView.js';
import { ResearchAiChainView } from './ResearchAiChainView.js';
import { ResearchReviewView } from './ResearchReviewView.js';
import { ResearchPublishView } from './ResearchPublishView.js';
import { useCloneResearchRequest } from './researchLibraryApi.js';
import { startResearchRun } from './researchOrchestratorApi.js';
import { useResearchRequest } from './intakeApi.js';
import { errorMessage } from '../api/client.js';
import { getCachedUser } from '../api/session.js';
import { useClearToastsOnScreenChange, useToast } from '../ui/ToastContext.js';

// `HostScreen` = `ResearchScreen` plus the host-local `library|aichain|publish`;
// `aichain` is deliberately NOT in the shared vocab: nothing may route into a paid
// screen from the server (open-by-status only yields intake|run|review).
// `FlowContext.cloneCriteria` is the machine-valued IntakeCriteria the clone
// endpoint returns verbatim (BUG-16: no legacy-blob remap).
import {
  DEFAULT_BROWSE_STATE,
  EMPTY_CTX,
  backToListLabel,
  fallbackBackScreen,
  fromHistoryState,
  toHistoryState,
  withIntakeDraft,
  type FlowContext,
  type HostScreen,
  type IntakeDraftSnapshot,
  type ResearchBrowseState,
} from './researchNavModel.js';

export interface ResearchSectionProps {
  /** Optional industry dictionary passed through to the Library filter. */
  readonly industries?: readonly string[];
}

export function ResearchSection({ industries = [] }: ResearchSectionProps): JSX.Element {
  const toast = useToast();
  const clone = useCloneResearchRequest();
  const [screen, setScreen] = useState<HostScreen>('library');
  const [ctx, setCtx] = useState<FlowContext>(EMPTY_CTX);
  // E06-F02-T11 — the browser's panel + History search/status/page, kept above the
  // (unmounting) ResearchLibraryView so returning to the list restores it.
  const [browse, setBrowse] = useState<ResearchBrowseState>(DEFAULT_BROWSE_STATE);
  // Bumped on every popstate so the browser remounts from the restored snapshot.
  const [restoreNonce, setRestoreNonce] = useState(0);
  // E06-F01-T15 — Research's internal screens are not app routes; sweep stale
  // toasts on these switches too (a red Intake error must not follow to Library).
  useClearToastsOnScreenChange(screen);

  // The latest screen/ctx/browse, for `go()` calls fired from async callbacks.
  const latest = useRef({ screen, ctx, browse });
  latest.current = { screen, ctx, browse };
  // E06-F02-T12 — depth of the current Research entry (Research screens pushed
  // before it in this visit); "Back" walks the browser history only while > 0.
  const depth = useRef(0);
  // E06-F02-T12 — what the mounted Intake form holds (criteria / title / project).
  const intakeDraft = useRef<IntakeDraftSnapshot | null>(null);

  /** The ctx to store for the CURRENT entry — an Intake entry keeps the typed form. */
  function currentEntryCtx(): FlowContext {
    const cur = latest.current;
    return cur.screen === 'intake' && intakeDraft.current
      ? withIntakeDraft(cur.ctx, intakeDraft.current)
      : cur.ctx;
  }

  /** Re-stamp the current history entry with the up-to-date screen/ctx/browse. */
  function stampCurrentEntry(): void {
    const cur = latest.current;
    window.history.replaceState(
      toHistoryState({
        screen: cur.screen,
        ctx: currentEntryCtx(),
        browse: cur.browse,
        depth: depth.current,
      }),
      '',
    );
  }

  /**
   * E06-F02-T11 — the one screen-switch path. Re-stamps the CURRENT history entry
   * with the up-to-date screen + browse state (the History search typed since the
   * entry was written), then pushes the new screen as a fresh entry, so browser
   * back returns exactly here. The URL stays `/research`.
   */
  function go(nextScreen: HostScreen, nextCtx: FlowContext): void {
    const cur = latest.current;
    if (typeof window !== 'undefined') {
      stampCurrentEntry();
      window.history.pushState(
        toHistoryState({
          screen: nextScreen,
          ctx: nextCtx,
          browse: cur.browse,
          depth: depth.current + 1,
        }),
        '',
      );
    }
    depth.current += 1;
    intakeDraft.current = null;
    latest.current = { screen: nextScreen, ctx: nextCtx, browse: cur.browse };
    setCtx(nextCtx);
    setScreen(nextScreen);
  }

  // E06-F02-T11 — browser Back/Forward inside Research restores the entry's screen,
  // its ctx and the browser state it was left with. Non-Research entries are the
  // shell's business (it switches the view away and unmounts this host).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPopState = (event: PopStateEvent): void => {
      const restored = fromHistoryState(event.state);
      if (!restored) return;
      depth.current = restored.depth ?? 0;
      intakeDraft.current = null;
      latest.current = {
        screen: restored.screen,
        ctx: restored.ctx,
        browse: restored.browse ?? latest.current.browse,
      };
      setCtx(restored.ctx);
      setScreen(restored.screen);
      if (restored.browse) setBrowse(restored.browse);
      setRestoreNonce((n) => n + 1);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const listLabel = backToListLabel(browse.panel);

  /**
   * E06-F02-T12 — the generic "Back": to the screen the recruiter came from. While an
   * earlier Research screen exists in this visit it is a real browser back (the
   * popstate handler restores that entry with its ctx and browse state); on the
   * first Research screen it goes to the logical parent instead, so "Back" never
   * leaves Research. The current entry is re-stamped first, so a later Forward
   * returns to it as it was (an Intake form keeps what was typed).
   */
  function goBack(): void {
    if (typeof window !== 'undefined' && depth.current > 0) {
      stampCurrentEntry();
      window.history.back();
      return;
    }
    const target = fallbackBackScreen(latest.current.screen);
    go(target, target === 'library' ? EMPTY_CTX : latest.current.ctx);
  }

  const backBar = (
    <div className="mb-4 flex items-center gap-5">
      <BackButton onClick={goBack} />
      <BackToLibrary onClick={goLibrary} label={listLabel} />
    </div>
  );

  /**
   * Route callback handed to ResearchLibraryView. It opens a History row / cloned
   * draft on the screen matching its status (`intake|run|review`); we set the host
   * screen + carry the ctx. A clone arrives as `intake` with prefilled criteria
   * (the machine-valued IntakeCriteria the clone endpoint returned) — we carry it so
   * the Intake screen opens prefilled (E06-F04-T05).
   */
  function handleOpenScreen(target: ResearchScreen, next: OpenScreenContext): void {
    go(target, {
      requestId: next.requestId,
      ...(next.status ? { status: next.status } : {}),
      ...(next.title !== undefined ? { title: next.title } : {}),
      ...(next.criteria ? { cloneCriteria: next.criteria } : {}),
      ...(next.sourceRequestId ? { sourceRequestId: next.sourceRequestId } : {}),
    });
  }

  /**
   * Back to the browser (after publish / cancel / done, and the "Back to History"
   * affordance). E06-F02-T11: lands on the panel + search/filter the recruiter
   * left, not a reset Library.
   */
  function goLibrary(): void {
    go('library', EMPTY_CTX);
  }

  /**
   * Intake → Run: a freshly created (or cloned+edited) request is a draft; kick
   * off the orchestrator run, then move to the Run screen. The Run screen polls
   * `/research/:id/status` and will render the queued/running state regardless of
   * the start outcome (the request is persisted as a draft either way). We start
   * the run against the exact returned id (no keyed-hook closure race).
   */
  function handleCreated(requestId: number): void {
    go('run', { requestId, status: 'queued' });
    void startResearchRun(requestId).catch(() => {
      // A start failure (e.g. an already-active run → CONFLICT, or a strategy
      // guard) is non-fatal to navigation: the Run screen shows whatever the
      // status poll reports. Surface it so the recruiter knows to check.
      toast.error('Could not start the run — check the request and retry from the Run screen.');
    });
  }

  /**
   * Intake → (draft saved): "Save draft" persists the request WITHOUT starting a
   * run. We return to the library so the draft appears under History (openable
   * later on the Request screen by open-by-status). The toast is shown by the form.
   */
  function handleDraftSaved(): void {
    goLibrary();
  }

  /**
   * Run → Intake, carrying the run's OWN criteria into the form (BUG-13). The Run
   * screen surfaces this via a single `onEditCriteria` prop consumed at three
   * call-sites — the persistent "Edit criteria" back-link, the low-yield "Broaden
   * criteria" button, and the suggested-relaxation chips — so wiring it once here
   * covers all three. Merely flipping `screen` to `intake` (the previous bug) left
   * `ctx.cloneCriteria` empty, so the form opened BLANK. We copy the request's
   * criteria (the same read-only server path the Library "Clone" action uses —
   * E12-F17-T04: it persists NO row), populate `ctx.cloneCriteria` + the '(copy)'
   * title, then navigate as a NEW request (`requestId: 0`); Run / Save draft from
   * the form creates exactly one request. The in-flight run stays untouched while
   * the recruiter re-tunes and re-launches.
   */
  function handleEditCriteria(): void {
    if (clone.isPending) return; // guard against a double-fire while the clone is in flight
    clone.mutate(ctx.requestId, {
      onSuccess: (res) => {
        go('intake', {
          requestId: 0,
          title: res.prefill.title,
          cloneCriteria: res.prefill.criteria,
          sourceRequestId: res.prefill.sourceRequestId,
        });
      },
      onError: () => {
        // Never leave the user on a stuck Run screen with no feedback (the
        // "silent failure" class of bug fixed elsewhere this cycle).
        toast.error('Could not load the criteria to edit — please try again.');
      },
    });
  }

  /**
   * E07-F04-T12 — a row's "collected in <request>" link: open that earlier request on
   * its Review list (the company was checked there, so that is where its data lives).
   * Navigation only — nothing paid starts from opening a screen.
   */
  function openEarlierRequest(requestId: number, title: string): void {
    go('review', { requestId, title });
  }

  switch (screen) {
    case 'intake':
      return (
        <div data-testid="research-section" data-screen="intake">
          {backBar}
          {/* Clone-prefill (E06-F04-T05): the clone endpoint returns the request's
              `criteria` verbatim as the machine-valued `IntakeCriteria` the form
              consumes, so a cloned request opens the Intake form prefilled directly
              (BUG-16: no legacy-blob remap). A cloned draft is unlinked, so no
              project is pre-selected. E12-F17-T07: a saved draft (requestId > 0) is
              loaded first and edited in place. */}
          <IntakeScreen
            ctx={ctx}
            onCreated={handleCreated}
            onDraftSaved={handleDraftSaved}
            onDraftChange={(draft) => {
              intakeDraft.current = draft;
            }}
            onOpenSimilar={(row) =>
              go(row.opens_to, { requestId: row.requestId, status: row.status, title: row.title })
            }
            onOpenDraft={(requestId) => go('intake', { requestId, status: 'draft' })}
          />
        </div>
      );

    case 'run':
      return (
        <div data-testid="research-section" data-screen="run">
          {backBar}
          <ResearchRunView
            requestId={ctx.requestId}
            title={ctx.title}
            onEditCriteria={handleEditCriteria}
            onReview={() => go('review', ctx)}
            onAiCheck={() => go('aichain', ctx)}
            onBackToLibrary={goLibrary}
          />
        </div>
      );

    case 'aichain':
      // The paid per-company chain lives here. Navigation into this screen costs
      // nothing — the view starts work only from an explicit click (single row) or
      // from its count-naming confirmation dialog (selected set).
      return (
        <div data-testid="research-section" data-screen="aichain">
          {backBar}
          <ResearchAiChainView
            requestId={ctx.requestId}
            title={ctx.title}
            onBack={() => go('run', ctx)}
            onReview={() => go('review', ctx)}
            onOpenRequest={openEarlierRequest}
          />
        </div>
      );

    case 'review':
      return (
        <div data-testid="research-section" data-screen="review">
          <ResearchReviewView
            key={ctx.requestId}
            requestId={ctx.requestId}
            onBack={() => go('run', ctx)}
            onPublish={() => go('publish', ctx)}
            onOpenRequest={openEarlierRequest}
            onBackToList={goLibrary}
            backToListLabel={listLabel}
            onBackPrevious={goBack}
          />
        </div>
      );

    case 'publish':
      return (
        <div data-testid="research-section" data-screen="publish">
          {backBar}
          <ResearchPublishView requestId={ctx.requestId} onBackToLibrary={goLibrary} />
        </div>
      );

    case 'library':
    default:
      return (
        <div data-testid="research-section" data-screen="library">
          <ResearchLibraryView
            key={restoreNonce}
            onOpenScreen={handleOpenScreen}
            industries={industries}
            initialBrowse={browse}
            onBrowseChange={setBrowse}
          />
        </div>
      );
  }
}

type IntakeScreenProps = Pick<
  ComponentProps<typeof IntakeForm>,
  'onCreated' | 'onDraftSaved' | 'onDraftChange' | 'onOpenSimilar' | 'onOpenDraft'
> & { readonly ctx: FlowContext };

/**
 * The Intake screen body. Three ways in:
 *   - New Request (requestId 0, no criteria) → an empty form that creates a row;
 *   - Clone / Run "Edit criteria" (requestId 0 + carried criteria) → prefilled, creates a row;
 *   - E12-F17-T07: a saved draft (requestId > 0) → its criteria / title / project
 *     come from the carried ctx when the entry was stamped (Back / Forward), else
 *     from GET /research/requests/:id; Save draft / Run then update THAT draft.
 * The carried criteria are the machine-valued IntakeCriteria the form consumes
 * (the clone endpoint returns `research_requests.criteria` verbatim).
 */
function IntakeScreen({ ctx, ...handlers }: IntakeScreenProps): JSX.Element {
  const draftId = ctx.requestId > 0 ? ctx.requestId : null;
  const needsLoad = draftId !== null && !ctx.cloneCriteria;
  // Read even when the form is seeded from a stamped ctx: the owner decides PATCH vs copy.
  const saved = useResearchRequest(draftId);

  if (needsLoad && saved.isError) {
    return (
      <div
        role="alert"
        data-testid="intake-draft-error"
        className="flex items-center gap-3 text-sm"
      >
        <span className="text-red-600">
          {errorMessage(saved.error, 'Could not load the draft')}
        </span>
        <button
          type="button"
          onClick={() => void saved.refetch()}
          className="font-semibold text-[var(--color-primary)] hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }
  // Seed the form only from a read completed AFTER this open: the form is keyed and
  // reads its initial props once, so mounting on the cached copy (e.g. reopened right
  // after Save draft, while the post-PATCH refetch is still in flight) would show the
  // pre-edit title / criteria and never re-seed (headed verify 2026-10-23, e2e step 5).
  if (needsLoad && (!saved.data || !saved.isFetchedAfterMount)) {
    return (
      <p data-testid="intake-draft-loading" className="text-sm text-slate-500">
        Loading the draft…
      </p>
    );
  }

  const req = needsLoad ? saved.data?.request : undefined;
  const initialCriteria: IntakeCriteria | undefined = ctx.cloneCriteria ?? req?.criteria;
  const initialTitle = ctx.cloneCriteria ? ctx.title : req?.title;
  const projectId = ctx.projectId ?? req?.projectId ?? null;
  // History is team-wide, but only the author edits a draft in place (the API 404s
  // anyone else): another user's draft opens prefilled and saves as the viewer's own.
  const draftOwner = saved.data?.request.ownerId;
  const foreignDraft =
    draftId !== null && draftOwner !== undefined && draftOwner !== (getCachedUser()?.id ?? null);
  const key =
    draftId !== null
      ? `draft-${draftId}`
      : ctx.cloneCriteria
        ? `clone-${ctx.sourceRequestId ?? 0}`
        : 'new';

  return (
    <>
      {foreignDraft && (
        <p data-testid="intake-foreign-draft" className="mb-3 text-sm text-slate-500">
          This draft belongs to another user — saving creates your own copy.
        </p>
      )}
      <IntakeForm
        key={key}
        initialCriteria={initialCriteria}
        initialTitle={initialTitle}
        projectId={projectId}
        draftRequestId={foreignDraft ? null : draftId}
        editingRequestId={draftId ?? ctx.sourceRequestId ?? null}
        {...handlers}
      />
    </>
  );
}

/**
 * The shared "Back to History / Library" affordance of the flow screens: returns to
 * the list with its search/filter intact (E06-F02-T11). The testid is kept from the
 * original "Back to library" button.
 */
function BackToLibrary({ onClick, label }: { onClick: () => void; label: string }): JSX.Element {
  return (
    <button
      type="button"
      data-testid="research-back-to-library"
      onClick={onClick}
      className="text-sm text-slate-500 hover:text-slate-800 flex items-center"
    >
      <i className="fa-solid fa-arrow-left mr-2" aria-hidden /> {label}
    </button>
  );
}

/** E06-F02-T12 — the generic "Back" to the previous screen of the flow. */
function BackButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      data-testid="research-back"
      onClick={onClick}
      className="text-sm text-slate-500 hover:text-slate-800 flex items-center"
    >
      <i className="fa-solid fa-arrow-left mr-2" aria-hidden /> Back
    </button>
  );
}

export default ResearchSection;
